/**
 * Renders a stream of page-builder blocks. In edit mode each block becomes
 * clickable and gains a small hover toolbar; otherwise it is plain output.
 */
import { useEffect } from "react";
import { ArrowDown, ArrowUp, Copy, Plus, Trash2 } from "lucide-react";
import { BlockView } from "@/components/cms/BlockRenderer";
import { useCmsEdit } from "@/components/cms/cms-edit";
import type { Block, PageDocument } from "@/lib/cms/blocks";

export function BlockCanvas({
  path,
  label,
  blocks,
}: {
  path: string;
  label: string;
  blocks: Block[];
}) {
  const cms = useCmsEdit();
  const { active, register } = cms;

  useEffect(() => {
    if (!active) return;
    register(path, label, { blocks } as PageDocument);
    // Re-runs when content first arrives; `register` keeps live edits intact.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, path, blocks.length]);

  if (!active) {
    return (
      <>
        {blocks.map((b) => (
          <BlockView key={b.id} block={b} />
        ))}
      </>
    );
  }

  const live = cms.regions[path]?.doc.blocks ?? blocks;

  const AddHere = ({ index }: { index: number }) => (
    <div className="relative z-10 flex justify-center py-1">
      <button
        type="button"
        onClick={() => cms.addBlock(path, "richText", index)}
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-[#1E5EFF] bg-white/90 px-3 py-1 text-[11px] font-semibold text-[#1E5EFF] opacity-60 shadow-sm transition hover:opacity-100"
      >
        <Plus className="h-3 w-3" /> Add section here
      </button>
    </div>
  );

  return (
    <div data-cms-canvas={path} className="relative">
      <AddHere index={0} />
      {live.map((b, i) => {
        const isSel = cms.selected?.path === path && cms.selected.id === b.id;
        return (
          <div key={b.id} data-cms-block={b.id}>
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                cms.select({ path, id: b.id });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") cms.select({ path, id: b.id });
              }}
              className={`group relative cursor-pointer rounded-lg outline-offset-2 transition ${
                isSel ? "outline outline-2 outline-[#1E5EFF]" : "hover:outline hover:outline-1 hover:outline-[#1E5EFF]/50"
              }`}
            >
              <div className="pointer-events-none absolute inset-0 z-0" />
              <div className="absolute right-2 top-2 z-20 hidden gap-1 rounded-md border border-[#D9D9D6] bg-white p-1 shadow group-hover:flex">
                <button
                  type="button"
                  aria-label="Move up"
                  onClick={(e) => {
                    e.stopPropagation();
                    cms.moveBlock(path, b.id, -1);
                  }}
                  className="rounded p-1 hover:bg-[#F0F0EE]"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  aria-label="Move down"
                  onClick={(e) => {
                    e.stopPropagation();
                    cms.moveBlock(path, b.id, 1);
                  }}
                  className="rounded p-1 hover:bg-[#F0F0EE]"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  aria-label="Duplicate"
                  onClick={(e) => {
                    e.stopPropagation();
                    cms.duplicateBlock(path, b.id);
                  }}
                  className="rounded p-1 hover:bg-[#F0F0EE]"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  aria-label="Delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    cms.removeBlock(path, b.id);
                  }}
                  className="rounded p-1 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <BlockView block={b} />
            </div>
            <AddHere index={i + 1} />
          </div>
        );
      })}
      {live.length === 0 ? (
        <div className="mx-auto my-10 max-w-2xl rounded-2xl border border-dashed border-[#D9D9D6] p-10 text-center text-sm text-[#2C2C2C]/60">
          Nothing here yet. Use “Add section here” or the panel on the right.
        </div>
      ) : null}
    </div>
  );
}

export default BlockCanvas;
